import api from '../services/api'

const MIME_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  pdf: 'application/pdf',
}

function getMimeType(fileName: string): string {
  const ext = fileName.split('.').pop()?.toLowerCase() || ''
  return MIME_TYPES[ext] || 'application/octet-stream'
}

// 生成安全的 ASCII 文件名（去除中文等特殊字符，避免 URL 编码膨胀导致数据库 url 列超长）
function generateSafeFileName(fileName: string): string {
  const extMatch = /\.([a-zA-Z0-9]+)$/.exec(fileName)
  const ext = extMatch ? extMatch[1].toLowerCase() : 'jpg'
  const base = `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  return `${base}.${ext}`
}

export interface SignatureResponse {
  url: string
  key: string
  bucket: string
  region: string
  endpoint?: string
  local?: boolean
  mimeType?: string
}

export interface UploadOptions {
  orderId: string
  type: string
  batchId?: string
  uploadedBy?: string
}

export interface UploadResult {
  url: string
  ossKey: string
  size: number
  mimeType: string
}

async function getSignature(
  orderId: string,
  type: string,
  fileName: string,
): Promise<SignatureResponse> {
  return await api.get('/files/signature', {
    params: { orderId, type, fileName },
  })
}

async function putToOss(signatureUrl: string, blob: Blob, contentType: string): Promise<void> {
  const response = await fetch(signatureUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType, 'x-oss-acl': 'public-read' },
    body: blob,
  })
  if (!response.ok) {
    throw new Error(`上传失败: ${response.status}`)
  }
}

async function uploadToLocal(
  orderId: string,
  fileType: string,
  fileName: string,
  blob: Blob,
): Promise<UploadResult> {
  const formData = new FormData()
  formData.append('file', blob, fileName)
  formData.append('orderId', orderId)
  formData.append('type', fileType)

  const response = await fetch('/v1/files/upload', {
    method: 'POST',
    body: formData,
  })
  if (!response.ok) {
    throw new Error(`本地上传失败: ${response.status}`)
  }
  const data = await response.json()
  return {
    url: data.url,
    ossKey: data.key,
    size: data.size || blob.size,
    mimeType: getMimeType(fileName),
  }
}

async function notifyCallback(
  options: UploadOptions,
  result: UploadResult,
  meta?: { width?: number; height?: number; duration?: number },
): Promise<void> {
  await api.post('/files/callback', {
    orderId: options.orderId,
    batchId: options.batchId,
    type: options.type,
    url: result.url,
    ossKey: result.ossKey,
    size: result.size,
    mimeType: result.mimeType,
    width: meta?.width,
    height: meta?.height,
    duration: meta?.duration,
    uploadedBy: options.uploadedBy,
  })
}

export async function uploadToOss(
  blob: Blob,
  fileName: string,
  options: UploadOptions,
  meta?: { width?: number; height?: number; duration?: number },
): Promise<UploadResult> {
  const safeFileName = generateSafeFileName(fileName)
  const contentType = getMimeType(safeFileName)
  const signature = await getSignature(options.orderId, options.type, safeFileName)

  if (signature.local) {
    const result = await uploadToLocal(options.orderId, options.type, safeFileName, blob)
    await notifyCallback(options, result, meta)
    return result
  }

  await putToOss(signature.url, blob, contentType)

  const publicUrl = signature.url.split('?')[0]
  const result: UploadResult = {
    url: publicUrl,
    ossKey: signature.key,
    size: blob.size,
    mimeType: contentType,
  }

  await notifyCallback(options, result, meta)
  return result
}

export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',')
  const mimeMatch = /:(.+?);/.exec(header)
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
  const binary = atob(base64)
  const arr = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    arr[i] = binary.charCodeAt(i)
  }
  return new Blob([arr], { type: mime })
}
